import { Audio } from 'expo-av';
import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';
import { API_URLS } from '../../api/config';

export interface AudioRecordingResult {
  transcription?: string;
  response?: string;
  audioUrl?: string;
  ttsUrl?: string;
  success?: boolean;
  error?: Error;
  audioBlob?: Blob;
}

class AudioRecordingService {
  private recording: Audio.Recording | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private isRecording: boolean = false;
  private sessionId: string | null = null;

  constructor() {
    this.sessionId = null;
  }

  async requestPermissions(): Promise<boolean> {
    if (Platform.OS === 'web') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch (error) {
        console.error('Error requesting web audio permissions:', error);
        return false;
      }
    } else {
      const { status } = await Audio.requestPermissionsAsync();
      return status === 'granted';
    }
  }

  async setupAudioMode(): Promise<void> {
    if (Platform.OS !== 'web') {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });
    }
  }

  async startRecording(): Promise<void> {
    try {
      console.log('Creating new recording object...');
      
      const hasPermission = await this.requestPermissions();
      if (!hasPermission) {
        throw new Error('Permission to access microphone was denied');
      }

      await this.setupAudioMode();

      if (Platform.OS === 'web') {
        // Different constraints for mobile and desktop browsers
        const constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Specific settings for mobile devices
            ...(this.isMobileBrowser() && {
              channelCount: 1,
              sampleRate: 44100,
            })
          }
        };

        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        
        // Choose appropriate MIME type based on browser support
        const mimeType = this.getSupportedMimeType();
        
        this.mediaRecorder = new MediaRecorder(this.mediaStream, {
          mimeType,
          audioBitsPerSecond: this.isMobileBrowser() ? 32000 : 128000
        });
        
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        // For mobile browsers, collect data more frequently
        const timeslice = this.isMobileBrowser() ? 1000 : undefined;
        this.mediaRecorder.start(timeslice);
      } else {
        const newRecording = new Audio.Recording();
        await newRecording.prepareToRecordAsync({
          android: {
            extension: '.m4a',
            outputFormat: Audio.AndroidOutputFormat.MPEG_4,
            audioEncoder: Audio.AndroidAudioEncoder.AAC,
            sampleRate: 44100,
            numberOfChannels: 2,
            bitRate: 128000,
          },
          ios: {
            extension: '.m4a',
            audioQuality: Audio.IOSAudioQuality.HIGH,
            sampleRate: 44100,
            numberOfChannels: 2,
            bitRate: 128000,
            linearPCMBitDepth: 16,
            linearPCMIsBigEndian: false,
            linearPCMIsFloat: false,
          },
        });

        await newRecording.startAsync();
        this.recording = newRecording;
      }

      this.isRecording = true;
      console.log('Recording started successfully');
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.reset();
      throw error;
    }
  }

  async stopRecording(): Promise<AudioRecordingResult> {
    console.log('Stopping recording...');
    
    if (Platform.OS === 'web') {
      if (!this.mediaRecorder || !this.isRecording) {
        console.warn('No active recording to stop');
        return {
          success: false,
          error: new Error('No recording in progress')
        };
      }

      try {
        return await new Promise<AudioRecordingResult>((resolve) => {
          const handleStop = () => {
            this.mediaRecorder?.removeEventListener('stop', handleStop);
            if (this.mediaStream) {
              this.mediaStream.getTracks().forEach(track => track.stop());
              this.mediaStream = null;
            }
            
            const audioBlob = new Blob(this.audioChunks, { 
              type: this.getSupportedMimeType() 
            });
            
            this.audioChunks = [];
            this.mediaRecorder = null;
            this.isRecording = false;
            
            resolve({
              success: true,
              audioBlob
            });
          };

          this.mediaRecorder?.addEventListener('stop', handleStop);
          if (this.mediaRecorder?.state === 'recording') {
            this.mediaRecorder?.stop();
          }
        });
      } catch (error) {
        console.error('Error stopping recording:', error);
        this.reset();
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Unknown error stopping recording')
        };
      }
    } else {
      if (!this.recording || !this.isRecording) {
        console.warn('No active recording to stop');
        return {
          success: false,
          error: new Error('No recording in progress')
        };
      }

      try {
        await this.recording.stopAndUnloadAsync();
        const uri = this.recording.getURI();
        this.recording = null;
        this.isRecording = false;
        
        return {
          success: true,
          audioUrl: uri || undefined
        };
      } catch (error) {
        console.error('Error stopping recording:', error);
        this.reset();
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Unknown error stopping recording')
        };
      }
    }
  }

  async processRecording(uri: string, language: string, sessionId: string): Promise<AudioRecordingResult> {
    try {
      const formData = new FormData();
      
      if (Platform.OS === 'web') {
        const audioBlob = new Blob(this.audioChunks, { type: this.getSupportedMimeType() });
        formData.append('audio', audioBlob, 'recording.webm');
      } else {
        formData.append('audio', {
          uri: uri,
          type: 'audio/m4a',
          name: 'recording.m4a'
        } as any);
      }
      
      formData.append('language', language);
      formData.append('session_id', sessionId);
      
      console.log('Sending audio to server...', {
        url: API_URLS.audioRecordingService,
        language,
        session_id: sessionId
      });

      const response = await fetch(API_URLS.audioRecordingService, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        transcription: result.transcription,
        response: result.response,
        audioUrl: result.audio_url,
        ttsUrl: result.tts_url
      };
    } catch (error) {
      console.error('Error processing recording:', error);
      throw error;
    }
  }

  isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  reset(): void {
    try {
      if (Platform.OS === 'web') {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.stop();
        }
        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach(track => track.stop());
        }
        this.mediaRecorder = null;
        this.mediaStream = null;
        this.audioChunks = [];
      } else {
        if (this.recording) {
          this.recording.stopAndUnloadAsync().catch(console.error);
          this.recording = null;
        }
      }
    } catch (error) {
      console.error('Error in reset:', error);
    } finally {
      this.isRecording = false;
    }
  }

  private isMobileBrowser(): boolean {
    if (Platform.OS !== 'web') return false;
    
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|iphone|ipad|ipod|webos/i.test(userAgent.toLowerCase());
  }

  private getSupportedMimeType(): string {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/aac',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // Fallback for iOS Safari
    if (isIOS) {
      return 'audio/mp4';
    }

    return 'audio/webm;codecs=opus';
  }
}

export const audioRecordingService = new AudioRecordingService();